/**
 * LanguageSelector — compact language switcher for the auth header.
 *
 * Renders a Cape Combobox. E2E tests interact via:
 *   - click `[data-testid="language-selector"]` to open the dropdown
 *   - click `[data-testid="lang-option-<code>"]` to select a language
 *
 * On selection:
 *  1. Updates i18next language (re-renders all useTranslation() consumers)
 *  2. Persists choice to localStorage as "portal_lang"
 *  3. Synchronously updates <html lang> and <html dir> before the i18n Promise
 *     resolves so RTL flip is immediate and matches the first translated render
 */
import {
  Combobox,
  ComboboxList,
  ComboboxOption,
  ComboboxSelect,
  ComboboxSelectValue,
  ComboboxTrigger,
} from "@deliveryhero/cape-core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { SUPPORTED_LANGUAGES, type SupportedLanguage } from "~/lib/i18n";
import * as styles from "./LanguageSelector.css";

const LANGUAGES = SUPPORTED_LANGUAGES.map((code) => ({
  code,
  labelKey: `lang_selector.${code}` as `lang_selector.${SupportedLanguage}`,
}));

export default function LanguageSelector() {
  const { i18n, t } = useTranslation();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  function handleSelect(lang: string) {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";

    void i18n.changeLanguage(lang);
    localStorage.setItem("portal_lang", lang);
  }

  const currentLang: SupportedLanguage =
    LANGUAGES.find((l) => l.code === i18n.language)?.code ??
    LANGUAGES.find((l) => l.code === i18n.language.split("-")[0])?.code ??
    "en";
  const currentLabel = LANGUAGES.find((l) => l.code === currentLang);

  // ComboboxTrigger uses HTMLInputElement synchronously (SSR-incompatible),
  // so on SSR / pre-hydration render a non-interactive placeholder with the
  // same dimensions as the Combobox. This avoids the flash of a native
  // <select> on first paint that doesn't match the prototype's Combobox.
  if (!mounted) {
    return (
      <div
        data-testid="language-selector"
        aria-label={t("lang_selector.label")}
        aria-busy="true"
        style={{
          minWidth: "var(--cp-sizing-5xlarge)",
          height: "var(--cp-sizing-medium)",
        }}
      />
    );
  }

  return (
    <Combobox
      selected={currentLang}
      onSelect={handleSelect}
      data-testid="language-selector"
    >
      <ComboboxTrigger>
        <ComboboxSelect size="xsmall" aria-label={t("lang_selector.label")} style={{ minWidth: "var(--cp-sizing-5xlarge)" }}>
          <ComboboxSelectValue placeholder={t("lang_selector.en")}>
            {currentLabel ? t(currentLabel.labelKey) : undefined}
          </ComboboxSelectValue>
        </ComboboxSelect>
      </ComboboxTrigger>
      <ComboboxList className={styles.popover}>
        {LANGUAGES.map(({ code, labelKey }) => (
          <ComboboxOption
            key={code}
            value={code}
            data-testid={`lang-option-${code}`}
            style={{ whiteSpace: "nowrap" }}
          >
            {t(labelKey)}
          </ComboboxOption>
        ))}
      </ComboboxList>
    </Combobox>
  );
}
